import { FC, ReactNode, useMemo, useState } from 'react'

import {
  Text,
  ErrorMessage,
  GreyGrey60,
  GreyGrey80,
  IconMagnifyingGlass,
  Image,
  Primary13Regular,
  Primary14SemiBold,
  Spinner,
  TextFieldInput,
  Center,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { usePopulateResolvedNodeDefinitions } from 'Automations/nodeDefinitions'
import { WorkflowCard } from '../WorkflowCard'
import { ButtonNewWorkflow } from '../ButtonNewWorkflow'
import ImgEmptyState from './EmptyState.png'

export const AutomationsList: FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const { useAutomationsDunning } = useFeatureFlags()

  // WorkflowCard reads the resolved registry (via useResolvedNodeDefinitions)
  // to render node icons, so we populate it once here and gate the list below.
  const definitionsReady = usePopulateResolvedNodeDefinitions()

  /**
   * Load all automations in a single page. Client-side search filters these
   * results, so we need all items loaded upfront. A limit of 100 is sufficient
   * for now — no customer is expected to exceed this in the near term. If that
   * changes, this should be replaced with server-side search.
   */
  const automationsQuery = useQuery(dashboardv99990101Client.getAutomations, {
    limit: 100
  })

  /**
   * Invoice sent (dunning) workflows stay hidden from the management screen
   * until the dunning feature is released. We resolve which automations use the
   * INVOICE_SENT trigger so they can be filtered out below; the query is skipped
   * entirely once the feature flag is enabled. The limit of 100 mirrors the
   * automations query above — no account is expected to exceed it while the
   * feature is still gated.
   */
  const dunningVersionsQuery = useQuery(
    dashboardv99990101Client.getAllAutomationVersions,
    { triggerType: 'INVOICE_SENT', limit: 100 },
    { enabled: !useAutomationsDunning }
  )

  const hiddenAutomationIds = useMemo(() => {
    if (useAutomationsDunning) return new Set<string>()
    return new Set(
      (dunningVersionsQuery.data?.items ?? []).map(
        version => version.automationId
      )
    )
  }, [useAutomationsDunning, dunningVersionsQuery.data])

  const allAutomations = useMemo(
    () =>
      (automationsQuery.data?.items ?? []).filter(
        automation => !hiddenAutomationIds.has(automation.id)
      ),
    [automationsQuery.data, hiddenAutomationIds]
  )

  const filteredAutomations = useMemo(() => {
    if (!searchQuery) return allAutomations
    const query = searchQuery.toLowerCase()
    return allAutomations.filter(
      item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
    )
  }, [allAutomations, searchQuery])

  /**
   * Block the list on the dunning filter query while it loads or if it errors:
   * without its result we cannot tell which automations are invoice sent
   * workflows, and showing them would defeat the feature gate.
   */
  const isDunningFilterPending =
    !useAutomationsDunning && dunningVersionsQuery.isPending
  const hasDunningFilterError =
    !useAutomationsDunning && dunningVersionsQuery.isError

  if (automationsQuery.isError || hasDunningFilterError) {
    return (
      <Wrapper>
        <ErrorMessage />
      </Wrapper>
    )
  }

  if (
    !automationsQuery.data ||
    automationsQuery.isPending ||
    isDunningFilterPending ||
    !definitionsReady
  ) {
    return (
      <Wrapper>
        <Spinner />
      </Wrapper>
    )
  }

  if (allAutomations.length === 0) {
    return (
      <Wrapper>
        <EmptyState />
      </Wrapper>
    )
  }

  return (
    <Flex flexDir="column" gap="16px">
      <Box maxWidth="300px" data-testid="automations.search">
        <TextFieldInput
          type="search"
          prefix={<IconMagnifyingGlass />}
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </Box>

      {filteredAutomations.length === 0 ? (
        <Center padding="24px" data-testid="automations.noSearchResults">
          <Flex flexDir="column" gap="4px" textAlign="center">
            <Text {...Primary13Regular} color={GreyGrey80}>
              No matching workflows
            </Text>
          </Flex>
        </Center>
      ) : (
        <Grid gridTemplateColumns="1fr 1fr 1fr" gap="16px">
          {filteredAutomations.map(item => (
            <WorkflowCard
              key={item.id}
              automationId={item.id}
              name={item.name}
              description={item.description}
              timestamp={item.createdAt}
            />
          ))}
        </Grid>
      )}
    </Flex>
  )
}

const Wrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return <Center padding="24px">{children}</Center>
}

const EmptyState: FC = () => {
  return (
    <Flex
      flexDir="column"
      width="280px"
      data-testid="automations.emptyState"
      gap="8px"
      alignItems="center"
    >
      <Center boxSize="200px">
        <Image src={ImgEmptyState} width="72px" height="81px" />
      </Center>

      <Flex flexDir="column" gap="16px">
        <Flex flexDir="column" gap="4px" textAlign="center">
          <Text {...Primary14SemiBold} color={GreyGrey80}>
            No workflows to show
          </Text>
          <Text {...Primary13Regular} color={GreyGrey60}>
            Create workflows to automate your business logic in Sequence.
          </Text>
        </Flex>

        <Flex justifyContent="center">
          <ButtonNewWorkflow />
        </Flex>
      </Flex>
    </Flex>
  )
}
